import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { CopyableId } from '../CopyableText';

import {
    getUser,
    listOrganizationMembers,
    listOrganizations,
    listUserRoles,
} from '../../api/resources';
import { EntityRef } from '../EntityRef';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { userLabel } from '../../lib/labels';
import { inlineOrganizationForMember, organizationByIdMap } from '../../lib/organizationLookup';
import { DetailField, DetailFieldGrid } from './DetailField';
import { DetailSection } from './DetailSection';

export function UserDetailPanel({ userId }: { userId: string }) {
    const { t } = useTranslation();
    const lookups = useAdminLookups();

    const userQ = useQuery({
        queryKey: ['user', userId],
        queryFn: () => getUser(userId),
    });

    const userRolesQ = useQuery({
        queryKey: ['user-roles', 'all'],
        queryFn: () => listUserRoles({ page: 1, limit: 500 }),
    });

    const membersQ = useQuery({
        queryKey: ['organization-members', 'all'],
        queryFn: () => listOrganizationMembers({ page: 1, limit: 500 }),
    });

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
        staleTime: 60_000,
    });

    const orgById = useMemo(
        () => organizationByIdMap(orgsQ.data?.data ?? []),
        [orgsQ.data],
    );

    const user = userQ.data;
    const platformRoles = (userRolesQ.data?.data ?? []).filter((ur) => ur.userId === userId);
    const orgMemberships = (membersQ.data?.data ?? []).filter((m) => m.userId === userId);

    if (!user) {
        return <Text c="dimmed">{t('common.loading.user')}</Text>;
    }

    const empty = t('common.empty');

    return (
        <>
            <DetailFieldGrid>
                <DetailField label={t('fields.name')} value={userLabel(user)} />
                <DetailField label={t('fields.email')} value={user.email ?? empty} />
                <DetailField label={t('fields.phone')} value={user.phone ?? empty} />
                <DetailField
                    label={t('fields.platformAdmin')}
                    value={
                        user.isPlatformAdmin ? (
                            <Badge color="violet" variant="light">
                                {t('common.yes')}
                            </Badge>
                        ) : (
                            t('common.no')
                        )
                    }
                />
                <DetailField label={t('fields.userId')} value={<CopyableId id={user.id} />} />
                <DetailField
                    label={t('fields.created')}
                    value={user.createdAt ? new Date(user.createdAt).toLocaleString() : empty}
                />
            </DetailFieldGrid>

            <DetailSection title={t('detail.platformRoles')} description={t('detail.platformRolesHint')}>
                {platformRoles.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noPlatformRoles')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.role')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {platformRoles.map((ur) => {
                                const role = lookups.resolveRole(ur.roleId, ur.role);
                                return (
                                    <Table.Tr key={ur.id}>
                                        <Table.Td>
                                            <EntityRef title={role.title} id={role.id} />
                                        </Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>

            <DetailSection
                title={t('detail.orgMemberships')}
                description={t('detail.orgMembershipsHint')}
            >
                {orgMemberships.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noOrgMemberships')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.organization')}</Table.Th>
                                <Table.Th>{t('fields.role')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {orgMemberships.map((m) => {
                                const org = lookups.resolveOrganization(
                                    m.organizationId,
                                    inlineOrganizationForMember(m, orgById),
                                );
                                const role = lookups.resolveRole(m.roleId, m.role);
                                return (
                                    <Table.Tr key={m.id}>
                                        <Table.Td>
                                            <EntityRef
                                                title={org.title}
                                                id={org.id}
                                                href={`/organizations?id=${m.organizationId}`}
                                                hideId
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <EntityRef title={role.title} id={role.id} />
                                        </Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>
        </>
    );
}
